import { useState, useEffect } from 'react';
import {
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    AreaChart, Area,
    RadialBarChart, RadialBar,
    LabelList,
} from 'recharts';
import { fetchSchoolsPerState, fetchSchoolsCount } from './ApiHandler/schoolFunctions';
import { fetchLabsCount } from './ApiHandler/labFunctions';
import { fetchSessionsPerMonth, fetchSessionsCount } from './ApiHandler/sessionFunctions';
import { fetchLabEquipmentCount, fetchEquipmentCount, FetchLabsWithMostAllocatedEquipment, FetchSchoolsWithMostAllocatedEquipment } from './ApiHandler/equipmentFunctions';
import { fetchArtifactsCounts, fetchTopContributors } from './ApiHandler/artifactsFunctions';
import { fetchSearchesCounts, fetchTopSearchedTags } from './ApiHandler/tagsFunctions';
import { fetchAllocatedUsedSpace } from './ApiHandler/settingsFunctions';
import SchoolsDetailModal from './DetailModals/SchoolsDetailModal';
import LabsDetailModal from './DetailModals/LabsDetailModal';
import EquipmentDetailModal from './DetailModals/EquipmentDetailModal';
import SessionsDetailModal from './DetailModals/SessionsDetailModal';

import ArtifactsDetailModal from './DetailModals/ArtifactsDetailModal';
import URLsDetailModal from './DetailModals/URLsDetailModal';
import SearchesYearDetailModal from './DetailModals/SearchesYearDetailModal';
import SearchesMonthDetailModal from './DetailModals/SearchesMonthDetailModal';
import Axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

/* ─── palette ─────────────────────────────────────────────── */
const COLORS = [
    { grad:'linear-gradient(135deg,#667eea,#764ba2)', solid:'#667eea', bg:'#667eea12' },
    { grad:'linear-gradient(135deg,#f093fb,#f5576c)', solid:'#f5576c', bg:'#f5576c12' },
    { grad:'linear-gradient(135deg,#4facfe,#00c6ff)', solid:'#4facfe', bg:'#4facfe12' },
    { grad:'linear-gradient(135deg,#43e97b,#38f9d7)', solid:'#43e97b', bg:'#43e97b12' },
    { grad:'linear-gradient(135deg,#fa709a,#fee140)', solid:'#fa709a', bg:'#fa709a12' },
    { grad:'linear-gradient(135deg,#a18cd1,#fbc2eb)', solid:'#a18cd1', bg:'#a18cd112' },
];
const PIE_COLORS = ['#8884d8','#82ca9d','#ff7300','#f59e0b','#ef4444','#10b981','#3b82f6','#8b5cf6','#ec4899','#14b8a6'];
const STATE_ABBR = { 'Andhra Pradesh':'AP','Arunachal':'AR','Assam':'AS','Bihar':'BR','Chhattisgarh':'CG','Goa':'GA','Gujrat':'GJ','Haryana':'HR','Himachal Pradesh':'HP','Jharkhand':'JH','Karnataka':'KA','Kerala':'KL','Madhya Pradesh':'MP','Maharashtra':'MH','Manipur':'MN','Meghalaya':'ML','Mizoram':'MZ','Nagaland':'NL','Odisha':'OD','Punjab':'PB','Rajasthan':'RJ','Sikkim':'SK','Tamil Nadu':'TN','Telangana':'TS','Tripura':'TR','Uttar Pradesh':'UP','Uttarakhand':'UK','West Bengal':'WB','Delhi':'DL','Jammu and Kashmir':'JK','Ladakh':'LA','Chandigarh':'CH','Puducherry':'PY' };

const getIcon = (name='') => {
    const l = name.toLowerCase();
    if (l.includes('atal'))         return 'bx bx-rocket';
    if (l.includes('sathee'))       return 'bx bx-book-open';
    if (l.includes('astrophysics') || l.includes('aaghaz') || l.includes('aghaaz')) return 'bx bx-planet';
    return 'bx bx-flask';
};

const fmt    = n => Number(n||0).toLocaleString('en-IN');
const fmtMo  = m => ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m.split('-')[1],10)-1];
const parsePics = raw => { if (Array.isArray(raw)) return raw; try { const p=JSON.parse(raw||'[]'); return Array.isArray(p)?p:[]; } catch{ return []; } };

/* ── image viewer ─────────────────────────────────────────── */
const ImageViewer = ({ imgs, idx, onClose, onNext, onPrev }) => (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.96)', zIndex:10000,
        display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:20 }}>
        <button onClick={onClose} style={{ position:'absolute', top:18, right:18, background:'rgba(255,255,255,.15)',
            border:'none', borderRadius:'50%', width:46, height:46, color:'#fff', fontSize:28, cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', zIndex:10001 }}>×</button>
        <div style={{ display:'flex', alignItems:'center', gap:14 }} onClick={e=>e.stopPropagation()}>
            {imgs.length>1 && <NavBtn onClick={onPrev}><i className="bx bx-chevron-left"/></NavBtn>}
            <div style={{ textAlign:'center' }}>
                <img src={imgs[idx]} alt="" style={{ maxWidth:'80vw', maxHeight:'74vh', borderRadius:14,
                    boxShadow:'0 20px 60px rgba(0,0,0,.55)' }}/>
                <p style={{ color:'#fff', marginTop:12, fontSize:13 }}>Image {idx+1} of {imgs.length}</p>
            </div>
            {imgs.length>1 && <NavBtn onClick={onNext}><i className="bx bx-chevron-right"/></NavBtn>}
        </div>
        {imgs.length>1 && (
            <div onClick={e=>e.stopPropagation()} style={{ display:'flex', gap:8, marginTop:14, padding:'8px 14px',
                background:'rgba(255,255,255,.1)', borderRadius:12, maxWidth:'88vw', overflowX:'auto' }}>
                {imgs.map((src,i)=>(
                    <img key={i} src={src} alt="" onClick={()=>onPrev(i)} style={{ width:48, height:48,
                        objectFit:'cover', borderRadius:8, cursor:'pointer', transition:'all .2s',
                        border: i===idx?'3px solid #fff':'3px solid transparent', opacity:i===idx?1:.45 }}/>
                ))}
            </div>
        )}
    </div>
);
const NavBtn = ({onClick,children}) => (
    <button onClick={onClick} style={{ background:'rgba(255,255,255,.15)', border:'none', borderRadius:'50%',
        width:44, height:44, color:'#fff', fontSize:22, cursor:'pointer', display:'flex',
        alignItems:'center', justifyContent:'center' }}>{children}</button>
);

/* ── session card ─────────────────────────────────────────── */
const SessionCard = ({ s, grad, openViewer }) => {
    const pics = parsePics(s.session_images);
    return (
        <div style={{ background:grad, borderRadius:16, padding:'18px 16px', color:'#fff',
            boxShadow:'0 8px 24px rgba(0,0,0,.17)', position:'relative', overflow:'hidden',
            transition:'transform .22s,box-shadow .22s' }}
            onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-4px)'; e.currentTarget.style.boxShadow='0 16px 40px rgba(0,0,0,.24)'; }}
            onMouseLeave={e=>{ e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,.17)'; }}>
            <div style={{ position:'absolute', top:-35, right:-35, width:110, height:110, borderRadius:'50%', background:'rgba(255,255,255,.08)' }}/>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:10 }}>
                <h4 style={{ margin:0, fontSize:14, fontWeight:800, lineHeight:1.35, flex:1, paddingRight:8 }}>{s.title}</h4>
                <span style={{ background: s.status==='Completed'?'#10b981':'#f59e0b',
                    padding:'3px 9px', borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:'nowrap', flexShrink:0 }}>
                    {s.status||'open'}
                </span>
            </div>
            {[['bx-user', s.session_host || (s.fname ? `${s.fname} ${s.lname}` : '')],['bx-calendar',`${new Date(s.session_date).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})} · ${s.session_time}`],
              ['bxs-school', s.school_name],['bx-test-tube', s.lab_name]].map(([ic,tx])=> tx &&
                <div key={ic} style={{ display:'flex', alignItems:'center', gap:6, marginBottom:5, fontSize:12, opacity:.93 }}>
                    <i className={`bx ${ic}`} style={{ fontSize:13, flexShrink:0 }}/><span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{tx}</span>
                </div>
            )}
            {(s.centre_code || s.state || s.district_name || s.sathee_mitra_name) && (
                <div style={{ background:'rgba(255,255,255,.14)', borderRadius:9, padding:'7px 10px', marginTop:8, fontSize:11, lineHeight: 1.4 }}>
                    {s.centre_code && <div><strong>Centre:</strong> {s.centre_code}</div>}
                    {s.state && <div><strong>State:</strong> {s.state}</div>}
                    {s.district_name && <div><strong>District:</strong> {s.district_name}</div>}
                    {s.sathee_mitra_name && <div><strong>Coord:</strong> {s.sathee_mitra_name}</div>}
                </div>
            )}
            {s.description && (
                <div style={{ marginTop: 10, fontSize: 12, opacity: 0.95, background: 'rgba(0,0,0,0.15)', padding: '8px 10px', borderRadius: 8, fontStyle: 'italic', lineHeight: 1.4 }}>
                    {s.description}
                </div>
            )}
            {pics.length>0 && (
                <div style={{ display:'flex', gap:4, marginTop:9, flexWrap:'wrap', alignItems:'center' }}>
                    {pics.slice(0,3).map((p,i)=>(
                        <img key={i} src={p} alt="" onClick={()=>openViewer(pics,i)}
                            style={{ width:46, height:46, objectFit:'cover', borderRadius:7,
                                border:'2px solid rgba(255,255,255,.3)', cursor:'pointer' }}/>
                    ))}
                    {pics.length>3 && <div onClick={()=>openViewer(pics)} style={{ width:46, height:46, borderRadius:7, background:'rgba(255,255,255,.2)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, cursor:'pointer' }}>+{pics.length-3}</div>}
                    <button onClick={()=>openViewer(pics)} style={{ width:46, height:46, borderRadius:7, background:'rgba(255,255,255,.2)', border:'2px solid rgba(255,255,255,.3)', color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <i className="bx bx-show" style={{ fontSize:18 }}/>
                    </button>
                </div>
            )}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:12, paddingTop:10, borderTop:'1px solid rgba(255,255,255,.18)' }}>
                <span style={{ fontSize:12, fontWeight:700 }}><i className="bx bx-group" style={{ marginRight:4 }}/>{fmt(s.student_count)} Students</span>
                <span style={{ fontSize:11, opacity:.7 }}>{new Date(s.session_date).toLocaleDateString('en-IN',{month:'short',day:'numeric'})}</span>
            </div>
        </div>
    );
};

/* ── per-lab sessions section ─────────────────────────────── */
const LabSection = ({ lab, ci, openViewer }) => {
    const [sessions, setSessions] = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [showAll,  setShowAll]  = useState(false);
    const c = COLORS[ci % COLORS.length];

    useEffect(()=>{
        let dead=false;
        (async()=>{
            try {
                const r = await Axios.get(`${API_URL}/sessions/fetchLatestSessionsByLabType`,
                    { params:{labTypeId:lab.lab_type_id,limit:50}, headers:{Authorization:localStorage.getItem('token')} });
                if (!dead && r.data.status==='success') setSessions(r.data.data);
            } catch{}
            finally{ if(!dead) setLoading(false); }
        })();
        return ()=>{ dead=true; };
    },[lab.lab_type_id]);

    const shown = showAll ? sessions : sessions.slice(0,6);

    return (
        <div style={{ marginBottom:24 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                padding:'11px 16px', borderRadius:13, background:c.bg,
                borderLeft:`4px solid ${c.solid}`, marginBottom:14 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:34, height:34, borderRadius:9, background:c.grad,
                        display:'flex', alignItems:'center', justifyContent:'center' }}>
                        <i className={getIcon(lab.lab_type_name)} style={{ fontSize:17, color:'#fff' }}/>
                    </div>
                    <div>
                        <h3 style={{ margin:0, fontSize:14, fontWeight:800, color:'#1a202c' }}>
                            🎓 {lab.lab_type_name} — Latest Sessions
                        </h3>
                        <p style={{ margin:0, fontSize:11, color:'#718096' }}>
                            {fmt(lab.total_sessions)} total · {fmt(lab.total_labs)} labs · {fmt(lab.total_students)} students
                        </p>
                    </div>
                </div>
                <div style={{ display:'flex', gap:7, alignItems:'center' }}>
                    <span style={{ background:c.solid, color:'#fff', borderRadius:20, padding:'2px 10px', fontSize:11, fontWeight:700 }}>{sessions.length} recent</span>
                    {sessions.length>6 && (
                        <button onClick={()=>setShowAll(s=>!s)}
                            style={{ background:'#fff', border:`2px solid ${c.solid}`, borderRadius:20,
                                padding:'2px 12px', color:c.solid, fontSize:11, fontWeight:700, cursor:'pointer' }}
                            onMouseEnter={e=>{ e.currentTarget.style.background=c.solid; e.currentTarget.style.color='#fff'; }}
                            onMouseLeave={e=>{ e.currentTarget.style.background='#fff'; e.currentTarget.style.color=c.solid; }}>
                            {showAll?'Show Less':`Show All (${sessions.length})`}
                        </button>
                    )}
                </div>
            </div>
            {loading
                ? <div style={{ textAlign:'center', padding:24, color:'#9ca3af' }}><i className="bx bx-loader-circle bx-spin" style={{ fontSize:28 }}/><p style={{ marginTop:8, fontSize:13 }}>Loading…</p></div>
                : <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
                    {shown.map(s=><SessionCard key={s.id} s={s} grad={c.grad} openViewer={openViewer}/>)}
                  </div>
            }
        </div>
    );
};

/* ── lab overview card ────────────────────────────────────── */
const LabCard = ({ lab, idx, active, onClick }) => {
    const c = COLORS[idx % COLORS.length];
    return (
        <div onClick={onClick} style={{ background:c.grad, borderRadius:18, padding:'18px 16px', color:'#fff',
            cursor:'pointer', position:'relative', overflow:'hidden',
            boxShadow: active ? `0 0 0 3px #fff,0 0 0 5px ${c.solid}` : '0 6px 20px rgba(0,0,0,0.15)',
            transition:'transform .2s,box-shadow .2s' }}
            onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-4px)'; }}
            onMouseLeave={e=>{ e.currentTarget.style.transform='none'; }}>
            <div style={{ position:'absolute', top:-25, right:-25, width:85, height:85, borderRadius:'50%', background:'rgba(255,255,255,.1)' }}/>
            <div style={{ position:'absolute', top:10, right:10, background:'rgba(255,255,255,.25)', borderRadius:'50%',
                width:25, height:25, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800 }}>{idx+1}</div>
            <i className={getIcon(lab.lab_type_name)} style={{ fontSize:32, marginBottom:8, display:'block', opacity:.9 }}/>
            <h4 style={{ margin:'0 0 12px', fontSize:13, fontWeight:800, lineHeight:1.3 }}>{lab.lab_type_name}</h4>
            <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
                {[['bx-calendar',fmt(lab.total_sessions),'Sessions'],['bx-group',fmt(lab.total_students),'Students'],['bx-test-tube',fmt(lab.total_labs),'Labs']].map(([ic,v,l])=>(
                    <div key={l} style={{ background:'rgba(255,255,255,.2)', borderRadius:9, padding:'4px 9px', textAlign:'center', minWidth:52 }}>
                        <i className={`bx ${ic}`} style={{ fontSize:11, display:'block', marginBottom:1 }}/>
                        <div style={{ fontSize:15, fontWeight:800 }}>{v}</div>
                        <div style={{ fontSize:10, opacity:.85 }}>{l}</div>
                    </div>
                ))}
            </div>
            <div style={{ marginTop:10, fontSize:11, opacity:.8, display:'flex', alignItems:'center', gap:4 }}>
                <i className={active?'bx bx-chevron-up-circle':'bx bx-chevron-down-circle'}/>
                {active ? 'Click to collapse' : 'Click to view sessions'}
            </div>
        </div>
    );
};

/* ── stat card ────────────────────────────────────────────── */
const StatCard = ({ icon, label, value, grad, onClick }) => (
    <div onClick={onClick} style={{ background:'#fff', borderRadius:16, padding:'16px 18px',
        display:'flex', alignItems:'center', gap:14, cursor: onClick?'pointer':'default',
        boxShadow:'0 2px 12px rgba(0,0,0,.07)', border:'1px solid #f0f0f0',
        transition:'transform .2s,box-shadow .2s' }}
        onMouseEnter={e=>{ if(onClick){ e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 8px 24px rgba(0,0,0,.12)'; }}}
        onMouseLeave={e=>{ e.currentTarget.style.transform='none'; e.currentTarget.style.boxShadow='0 2px 12px rgba(0,0,0,.07)'; }}>
        <div style={{ width:48, height:48, borderRadius:13, background:grad,
            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <i className={`bx ${icon}`} style={{ fontSize:22, color:'#fff' }}/>
        </div>
        <div>
            <p style={{ margin:0, fontSize:11, color:'#9ca3af', fontWeight:600, textTransform:'uppercase', letterSpacing:.5 }}>{label}</p>
            <p style={{ margin:'2px 0 0', fontSize:22, fontWeight:800, color:'#1a202c' }}>{fmt(value)}</p>
        </div>
    </div>
);

/* ── section heading ──────────────────────────────────────── */
const SH = ({ icon, title, badge, sub }) => (
    <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14 }}>
        <i className={`bx ${icon}`} style={{ fontSize:21, color:'#667eea' }}/>
        <div>
            <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                <h2 style={{ margin:0, fontSize:17, fontWeight:800, color:'#1a202c' }}>{title}</h2>
                {badge && <span style={{ background:'#667eea', color:'#fff', borderRadius:12, padding:'1px 8px', fontSize:11, fontWeight:700 }}>{badge}</span>}
            </div>
            {sub && <p style={{ margin:0, fontSize:12, color:'#9ca3af' }}>{sub}</p>}
        </div>
    </div>
);

const Card = ({ children, style={} }) => (
    <div style={{ background:'#fff', borderRadius:18, padding:20, boxShadow:'0 2px 16px rgba(0,0,0,.07)', ...style }}>{children}</div>
);
const Th = ({c}) => <th style={{ padding:'7px 10px', textAlign:'left', fontSize:11, fontWeight:700, color:'#9ca3af', textTransform:'uppercase', letterSpacing:.5, borderBottom:'2px solid #f3f4f6' }}>{c}</th>;
const Td = ({children}) => <td style={{ padding:'8px 10px', color:'#374151', fontSize:13, verticalAlign:'middle' }}>{children}</td>;

/* ── custom chart tooltip ─────────────────────────────────── */
const ChartTooltip = ({ active, payload, label, suffix='' }) => {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background:'#1a202c', borderRadius:10, padding:'8px 14px',
            boxShadow:'0 8px 24px rgba(0,0,0,.3)', border:'1px solid rgba(255,255,255,.08)' }}>
            {label && <p style={{ margin:'0 0 4px', fontSize:11, color:'#9ca3af', fontWeight:600 }}>{label}</p>}
            {payload.map((p,i)=>(
                <p key={i} style={{ margin:0, fontSize:13, fontWeight:700, color: p.color||'#fff' }}>
                    {Number(p.value).toLocaleString('en-IN')}{suffix}
                    {payload.length>1 && <span style={{ color:'#9ca3af', fontWeight:400, marginLeft:4 }}>{p.name}</span>}
                </p>
            ))}
        </div>
    );
};

/* ── chart icon header ────────────────────────────────────── */
const ChartHeader = ({ title, sub, iconClass, grad }) => (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <div>
            <h3 style={{ margin:0, fontSize:14, fontWeight:800, color:'#1a202c' }}>{title}</h3>
            <p style={{ margin:'2px 0 0', fontSize:11, color:'#9ca3af' }}>{sub}</p>
        </div>
        <div style={{ width:36, height:36, borderRadius:10, background:grad,
            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <i className={iconClass} style={{ fontSize:18, color:'#fff' }}/>
        </div>
    </div>
);

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const Home = ({ handleClick, setIsSidebarOpen }) => {
    const [totalDocsCount,       setTotalDocsCount]       = useState(0);
    const [totalUrlsCount,       setTotalUrlsCount]       = useState(0);
    const [totalSearches,        setTotalSearches]        = useState(0);
    const [currentMonthSearches, setCurrentMonthSearches] = useState(0);
    const [usedSpace,            setUsedSpace]            = useState(0);
    const [totalAllocatedSpace,  setTotalAllocatedSpace]  = useState(0);
    const [searchedTags,         setSearchedTags]         = useState([]);
    const [contributors,         setContributors]         = useState([]);
    const [schoolsData,          setSchoolsData]          = useState([]);
    const [sessionData,          setSessionData]          = useState([]);
    const [labEquipmentData,     setLabEquipmentData]     = useState([]);
    const [totalSchoolsCount,    setTotalSchoolsCount]    = useState(0);
    const [totalLabsCount,       setTotalLabsCount]       = useState(0);
    const [totalEquipmentCount,  setTotalEquipmentCount]  = useState(0);
    const [totalSessionsCount,   setTotalSessionsCount]   = useState(0);

    const [labsWithMostEquip,    setLabsWithMostEquip]    = useState([]);
    const [schoolsWithMostEquip, setSchoolsWithMostEquip] = useState([]);

    const [labTypes,        setLabTypes]        = useState([]);
    const [activeLabId,     setActiveLabId]     = useState(null);
    const [univSessions,    setUnivSessions]    = useState([]);
    const [univShowAll,     setUnivShowAll]     = useState(false);
    const [roleId,          setRoleId]          = useState(null);
    const [mentorLabTypeId, setMentorLabTypeId] = useState(null);

    const [schoolsModal,   setSchoolsModal]   = useState(false);
    const [labsModal,      setLabsModal]      = useState(false);
    const [equipModal,     setEquipModal]     = useState(false);
    const [sessionsModal,  setSessionsModal]  = useState(false);

    const [artifactsModal, setArtifactsModal] = useState(false);
    const [urlsModal,      setUrlsModal]      = useState(false);
    const [searchYrModal,  setSearchYrModal]  = useState(false);
    const [searchMoModal,  setSearchMoModal]  = useState(false);

    const [viewImgs, setViewImgs] = useState([]);
    const [viewIdx,  setViewIdx]  = useState(0);
    const openViewer  = (imgs, i=0) => { setViewImgs(imgs); setViewIdx(i); };
    const closeViewer = () => setViewImgs([]);
    const nextImg = () => setViewIdx(p=>(p+1)%viewImgs.length);
    const prevImg = (i) => typeof i==='number' ? setViewIdx(i) : setViewIdx(p=>(p-1+viewImgs.length)%viewImgs.length);

    const modal = setter => { if (setIsSidebarOpen) setIsSidebarOpen(false); setter(true); };

    useEffect(()=>{
        try {
            const t = localStorage.getItem('token');
            if (t) {
                const p = JSON.parse(atob(t.split('.')[1]));
                setRoleId(p.role_id);
                if (p.role_id === 2 && p.assignedLab) {
                    Axios.get(`${API_URL}/labs/getLabTypeByLabId/${p.assignedLab}`,
                        { headers:{ Authorization: localStorage.getItem('token') } }
                    ).then(r => {
                        if (r.data.status === 'success') setMentorLabTypeId(r.data.lab_type_id);
                    }).catch(()=>{});
                }
            }
        } catch{}
    },[]);

    const isMentor   = roleId === 4;

    const visibleLabTypes = isMentor && mentorLabTypeId
        ? labTypes.filter(l => l.lab_type_id === mentorLabTypeId)
        : labTypes;

    useEffect(()=>{
        fetchArtifactsCounts(setTotalDocsCount, setTotalUrlsCount);
        fetchSearchesCounts(setTotalSearches, setCurrentMonthSearches);
        fetchAllocatedUsedSpace(setTotalAllocatedSpace, setUsedSpace, null, null, null);
        fetchTopSearchedTags(setSearchedTags);
        fetchTopContributors(setContributors);
        fetchSchoolsPerState(setSchoolsData);
        fetchSessionsPerMonth(setSessionData);
        fetchLabEquipmentCount(setLabEquipmentData);
        fetchSchoolsCount(setTotalSchoolsCount);
        fetchLabsCount(setTotalLabsCount);
        fetchEquipmentCount(setTotalEquipmentCount);
        fetchSessionsCount(setTotalSessionsCount);
        FetchLabsWithMostAllocatedEquipment(setLabsWithMostEquip);
        FetchSchoolsWithMostAllocatedEquipment(setSchoolsWithMostEquip);

        const H = { Authorization: localStorage.getItem('token') };

        Axios.get(`${API_URL}/labs/dashboard-stats`, {headers:H}).then(r=>{ if(r.data.status==='success') setLabTypes(r.data.data); }).catch(()=>{});
        Axios.get(`${API_URL}/sessions/fetchLatestSessions?limit=50`,{headers:H}).then(r=>{ if(r.data.status==='success') setUnivSessions(r.data.data); }).catch(()=>{});
    },[]);

    const usedPct   = totalAllocatedSpace>0 ? (usedSpace/totalAllocatedSpace)*100 : 0;
    const schoolPie = schoolsData.map(i=>({ name:i.state,  value:i.school_count }));
    const sessPie   = sessionData.map(i=>({ name:fmtMo(i.month), value:i.session_count }));
    const ULIMIT    = 6;
    const univShown = univShowAll ? univSessions : univSessions.slice(0,ULIMIT);

    const sessCumulative = sessPie.reduce((acc,cur,i)=>[
        ...acc,
        { ...cur, cumulative:(acc[i-1]?.cumulative||0)+cur.value }
    ],[]);

    const toggleLab = id => {
        setActiveLabId(p=>p===id?null:id);
        setTimeout(()=>{ document.getElementById(`ls-${id}`)?.scrollIntoView({behavior:'smooth',block:'start'}); },100);
    };

    return (
        <main style={{ padding:'0 0 48px' }}>

            {/* ═══ TOP STAT CARDS ═══════════════════════════════ */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))', gap:14, marginBottom:28 }}>
                <StatCard icon="bxs-school"   label="Total Schools"   value={totalSchoolsCount}   grad="linear-gradient(135deg,#667eea,#764ba2)" onClick={()=>modal(setSchoolsModal)}/>
                <StatCard icon="bx-test-tube" label="Total Labs"      value={totalLabsCount}      grad="linear-gradient(135deg,#f093fb,#f5576c)" onClick={()=>modal(setLabsModal)}/>
                <StatCard icon="bx-box"       label="Equipment"       value={totalEquipmentCount} grad="linear-gradient(135deg,#4facfe,#00c6ff)" onClick={()=>modal(setEquipModal)}/>
                <StatCard icon="bx-calendar"  label="Sessions"        value={totalSessionsCount}  grad="linear-gradient(135deg,#43e97b,#38f9d7)" onClick={()=>modal(setSessionsModal)}/>

            </div>

            {/* ═══ LAB OVERVIEW CARDS ═══════════════════════════ */}
            {visibleLabTypes.length>0 && (<>
                <SH icon="bx-grid-alt" title="Labs Overview" badge={`${visibleLabTypes.length} Lab Types`} sub="Click any lab card below to view its latest sessions"/>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(210px,1fr))', gap:14, marginBottom:28 }}>
                    {visibleLabTypes.map((lab,i)=>(
                        <LabCard key={lab.lab_type_id} lab={lab} idx={i}
                            active={activeLabId===lab.lab_type_id}
                            onClick={()=>toggleLab(lab.lab_type_id)}/>
                    ))}
                </div>
            </>)}

            {/* ═══ PER-LAB SESSION SECTIONS ═════════════════════ */}
            {visibleLabTypes.length>0 && (<>
                <SH icon="bx-layer" title="Sessions by Lab Type" sub="Each lab type's sessions displayed separately"/>
                {visibleLabTypes.map((lab,i)=>(
                    <div key={lab.lab_type_id} id={`ls-${lab.lab_type_id}`}
                        style={{ scrollMarginTop:80,
                            border: activeLabId===lab.lab_type_id ? `2px solid ${COLORS[i%COLORS.length].solid}` : '2px solid transparent',
                            borderRadius:16, padding: activeLabId===lab.lab_type_id ? '2px' : '0',
                            transition:'border .2s' }}>
                        <LabSection lab={lab} ci={i} openViewer={openViewer}/>
                    </div>
                ))}
            </>)}

            {/* ═══ ALL LABS SECTION ═════════════════════════════ */}
            <div style={{ marginBottom:28 }}>
                <SH icon="bx-globe" title="All Labs" sub="Latest sessions from every lab type in one view"/>
                <Card>
                    <div style={{ background:'linear-gradient(135deg,#2d3748,#4a5568)', borderRadius:12, padding:'13px 18px',
                        display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:18 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                            <div style={{ width:34, height:34, borderRadius:9, background:'rgba(255,255,255,.15)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <i className="bx bx-globe" style={{ fontSize:18, color:'#fff' }}/>
                            </div>
                            <div>
                                <p style={{ margin:0, fontSize:14, fontWeight:700, color:'#fff' }}>All Labs</p>
                                <p style={{ margin:0, fontSize:11, color:'rgba(255,255,255,.6)' }}>Most recent across every lab type</p>
                            </div>
                        </div>
                        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                            <span style={{ background:'rgba(255,255,255,.18)', color:'#fff', borderRadius:20, padding:'2px 10px', fontSize:12, fontWeight:700 }}>{univSessions.length} sessions</span>
                            {univSessions.length>ULIMIT && (
                                <button onClick={()=>setUnivShowAll(s=>!s)}
                                    style={{ background:'rgba(255,255,255,.15)', border:'2px solid rgba(255,255,255,.4)', borderRadius:20, padding:'2px 12px', color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer' }}>
                                    {univShowAll?'Show Less':`Show All (${univSessions.length})`}
                                </button>
                            )}
                        </div>
                    </div>
                    {univSessions.length>0
                        ? <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:16 }}>
                            {univShown.map(s=><SessionCard key={s.id} s={s} grad="linear-gradient(135deg,#2d3748,#4a5568)" openViewer={openViewer}/>)}
                          </div>
                        : <div style={{ textAlign:'center', padding:'40px 20px', color:'#9ca3af' }}>
                            <i className="bx bx-calendar-x" style={{ fontSize:48 }}/><p>No sessions yet</p>
                          </div>
                    }
                </Card>
            </div>

            {/* ═══ CHARTS ═══════════════════════════════════════ */}
            <SH icon="bx-bar-chart-alt-2" title="Analytics" sub="Sessions, schools, lab distribution and trends"/>

            {/* ROW 1 — Bar Chart + Donut Chart */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:18, marginBottom:18 }}>
                <Card>
                    <ChartHeader title="Sessions per Month" sub="Monthly session activity"
                        iconClass="bx bx-bar-chart-alt-2" grad="linear-gradient(135deg,#667eea,#764ba2)"/>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={sessPie} margin={{ top:4, right:4, left:-18, bottom:0 }} barSize={28}>
                            <defs>
                                <linearGradient id="barGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%"  stopColor="#667eea" stopOpacity={1}/>
                                    <stop offset="100%" stopColor="#764ba2" stopOpacity={0.7}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
                            <XAxis dataKey="name" tick={{ fontSize:11, fill:'#9ca3af' }} axisLine={false} tickLine={false}/>
                            <YAxis tick={{ fontSize:11, fill:'#9ca3af' }} axisLine={false} tickLine={false}/>
                            <Tooltip content={<ChartTooltip suffix=" sessions"/>}/>
                            <Bar dataKey="value" fill="url(#barGrad)" radius={[6,6,0,0]} name="Sessions">
                                <LabelList dataKey="value" position="top" style={{ fontSize:10, fill:'#667eea', fontWeight:700 }}/>
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </Card>

                <Card>
                    <ChartHeader title="Schools by State" sub="Geographic distribution"
                        iconClass="bx bxs-school" grad="linear-gradient(135deg,#f093fb,#f5576c)"/>
                    <ResponsiveContainer width="100%" height={260}>
                        <PieChart>
                            <Pie data={schoolPie} cx="42%" cy="50%"
                                innerRadius={68} outerRadius={105}
                                dataKey="value" paddingAngle={2} nameKey="name">
                                {schoolPie.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                            </Pie>
                            <Tooltip content={<ChartTooltip suffix=" schools"/>}/>
                            <Legend layout="vertical" align="right" verticalAlign="middle"
                                formatter={v=><span style={{ fontSize:10, color:'#4a5568' }}>{STATE_ABBR[v]||v}</span>}
                                wrapperStyle={{ fontSize:10, maxHeight:240, overflowY:'auto', paddingLeft:8 }}/>
                        </PieChart>
                    </ResponsiveContainer>
                    <p style={{ margin:'4px 0 0', fontSize:11, color:'#9ca3af', textAlign:'center' }}>
                        {schoolPie.reduce((a,b)=>a+b.value,0).toLocaleString('en-IN')} schools across {schoolPie.length} states
                    </p>
                </Card>
            </div>

            {/* ROW 2 — Area Chart + Radial Bar */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))', gap:18, marginBottom:28 }}>
                <Card>
                    <ChartHeader title="Session Trend" sub="Cumulative growth over months"
                        iconClass="bx bx-trending-up" grad="linear-gradient(135deg,#43e97b,#38f9d7)"/>
                    <ResponsiveContainer width="100%" height={230}>
                        <AreaChart data={sessCumulative} margin={{ top:4, right:4, left:-18, bottom:0 }}>
                            <defs>
                                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%"  stopColor="#43e97b" stopOpacity={0.35}/>
                                    <stop offset="95%" stopColor="#38f9d7" stopOpacity={0.03}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
                            <XAxis dataKey="name" tick={{ fontSize:11, fill:'#9ca3af' }} axisLine={false} tickLine={false}/>
                            <YAxis tick={{ fontSize:11, fill:'#9ca3af' }} axisLine={false} tickLine={false}/>
                            <Tooltip content={<ChartTooltip suffix=" total sessions"/>}/>
                            <Area type="monotone" dataKey="cumulative" stroke="#43e97b" strokeWidth={2.5}
                                fill="url(#areaGrad)"
                                dot={{ r:3, fill:'#43e97b', strokeWidth:0 }}
                                activeDot={{ r:5, fill:'#43e97b' }} name="Cumulative"/>
                        </AreaChart>
                    </ResponsiveContainer>
                </Card>

                <Card>
                    <ChartHeader title="Lab Type Distribution" sub="Sessions by lab type"
                        iconClass="bx bx-doughnut-chart" grad="linear-gradient(135deg,#4facfe,#00c6ff)"/>
                    {visibleLabTypes.length > 0 ? (
                        <>
                            <ResponsiveContainer width="100%" height={190}>
                                <RadialBarChart cx="50%" cy="50%"
                                    innerRadius="20%" outerRadius="90%"
                                    startAngle={180} endAngle={-180}
                                    data={visibleLabTypes.map((lab,i)=>({
                                        name:  lab.lab_type_name,
                                        value: Number(lab.total_sessions)||0,
                                        fill:  PIE_COLORS[i % PIE_COLORS.length],
                                    }))}>
                                    <RadialBar dataKey="value" cornerRadius={6} background={{ fill:'#f3f4f6' }}/>
                                    <Tooltip content={<ChartTooltip suffix=" sessions"/>}/>
                                </RadialBarChart>
                            </ResponsiveContainer>
                            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px 16px', justifyContent:'center', marginTop:8 }}>
                                {visibleLabTypes.map((lab,i)=>(
                                    <div key={lab.lab_type_id} style={{ display:'flex', alignItems:'center', gap:5 }}>
                                        <div style={{ width:8, height:8, borderRadius:'50%', background:PIE_COLORS[i%PIE_COLORS.length], flexShrink:0 }}/>
                                        <span style={{ fontSize:11, color:'#4a5568' }}>{lab.lab_type_name}</span>
                                        <span style={{ fontSize:11, fontWeight:700, color:'#1a202c' }}>
                                            {Number(lab.total_sessions||0).toLocaleString('en-IN')}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div style={{ textAlign:'center', padding:'40px 0', color:'#9ca3af' }}>
                            <i className="bx bx-loader-circle bx-spin" style={{ fontSize:32 }}/>
                            <p style={{ marginTop:8, fontSize:13 }}>Loading lab data…</p>
                        </div>
                    )}
                </Card>
            </div>

            {/* ═══ BOTTOM STATS ═════════════════════════════════ */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(190px,1fr))', gap:14, marginBottom:28 }}>
                <StatCard icon="bx-store-alt"  label="Artifacts (Docs)" value={totalDocsCount}       grad="linear-gradient(135deg,#667eea,#764ba2)" onClick={()=>modal(setArtifactsModal)}/>
                <StatCard icon="bx-link"       label="URLs Shared"      value={totalUrlsCount}       grad="linear-gradient(135deg,#f093fb,#f5576c)" onClick={()=>modal(setUrlsModal)}/>
                <StatCard icon="bx-search"     label={`Searches ${new Date().getFullYear()}`}         value={totalSearches}        grad="linear-gradient(135deg,#4facfe,#00c6ff)" onClick={()=>modal(setSearchYrModal)}/>
                <StatCard icon="bx-search-alt" label={`Searches ${new Date().toLocaleString('default',{month:'short'})}`} value={currentMonthSearches} grad="linear-gradient(135deg,#43e97b,#38f9d7)" onClick={()=>modal(setSearchMoModal)}/>
            </div>

            {/* ═══ STORAGE ══════════════════════════════════════ */}
            <Card style={{ marginBottom:28 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                    <span style={{ fontSize:13, fontWeight:600, color:'#4a5568' }}>Storage Used</span>
                    <span style={{ fontSize:13, color:'#718096' }}>{usedSpace.toFixed(2)} GB / {totalAllocatedSpace.toFixed(2)} GB</span>
                </div>
                <div style={{ height:10, background:'#e2e8f0', borderRadius:20, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${Math.min(usedPct,100)}%`, borderRadius:20, transition:'width .5s',
                        background: usedPct>80 ? 'linear-gradient(90deg,#f59e0b,#ef4444)' : 'linear-gradient(90deg,#667eea,#764ba2)' }}/>
                </div>
                <p style={{ margin:'5px 0 0', fontSize:12, color:'#9ca3af' }}>{usedPct.toFixed(1)}% used</p>
            </Card>

            {/* ═══ TAGS + CONTRIBUTORS ══════════════════════════ */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:18, marginBottom:28 }}>
                <Card>
                    <h3 style={{ margin:'0 0 12px', fontSize:14, fontWeight:700, color:'#2d3748' }}>🔍 Top 10 Searched Tags</h3>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <tbody>{searchedTags.map(t=>(
                            <tr key={t.tag_name} style={{ borderBottom:'1px solid #f3f4f6' }}>
                                <Td>{t.tag_name}</Td>
                                <Td><b style={{ color:'#667eea' }}>{t.search_count}</b></Td>
                            </tr>
                        ))}</tbody>
                    </table>
                </Card>
                <Card>
                    <h3 style={{ margin:'0 0 12px', fontSize:14, fontWeight:700, color:'#2d3748' }}>🏆 Top 10 Contributors</h3>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <tbody>{contributors.map(c=>(
                            <tr key={c.owner_author_id} style={{ borderBottom:'1px solid #f3f4f6' }}>
                                <Td>{c.owner_author_id}</Td>
                                <Td><b style={{ color:'#10b981' }}>{c.doc_count}</b></Td>
                            </tr>
                        ))}</tbody>
                    </table>
                </Card>
            </div>

            {/* ═══ EQUIPMENT OVERVIEW (moved to bottom) ════════ */}
            <SH icon="bx-box" title="Equipment Overview" sub="Allocation by lab and school"/>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))', gap:18, marginBottom:28 }}>
                <Card>
                    <h3 style={{ margin:'0 0 12px', fontSize:14, fontWeight:700, color:'#2d3748' }}>Lab Equipment Allocation</h3>
                    <div style={{ overflowX:'auto' }}>
                        <table style={{ width:'100%', borderCollapse:'collapse' }}>
                            <thead><tr><Th c="Lab"/><Th c="Equipment"/><Th c="Count"/></tr></thead>
                            <tbody>{labEquipmentData.map((l,i)=>(
                                <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                                    <Td>{l.lab_name}</Td>
                                    <Td>{l.equipment_names.length ? l.equipment_names.join(', ') : '—'}</Td>
                                    <Td><b style={{ color:'#667eea' }}>{l.equipment_count}</b></Td>
                                </tr>
                            ))}</tbody>
                        </table>
                    </div>
                </Card>
                <Card>
                    <h3 style={{ margin:'0 0 12px', fontSize:14, fontWeight:700, color:'#2d3748' }}>Schools — Most Equipment</h3>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <tbody>{schoolsWithMostEquip.map((s,i)=>(
                            <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                                <Td>{s.school_name}</Td>
                                <Td><b style={{ color:'#10b981' }}>{s.total_allocated}</b></Td>
                            </tr>
                        ))}</tbody>
                    </table>
                </Card>
                <Card>
                    <h3 style={{ margin:'0 0 12px', fontSize:14, fontWeight:700, color:'#2d3748' }}>Labs — Most Equipment</h3>
                    <table style={{ width:'100%', borderCollapse:'collapse' }}>
                        <tbody>{labsWithMostEquip.map((l,i)=>(
                            <tr key={i} style={{ borderBottom:'1px solid #f3f4f6' }}>
                                <Td>{l.lab_name}</Td>
                                <Td><b style={{ color:'#f59e0b' }}>{l.total_allocated}</b></Td>
                            </tr>
                        ))}</tbody>
                    </table>
                </Card>
            </div>

            {/* ═══ MODALS ═══════════════════════════════════════ */}
            <SchoolsDetailModal        isOpen={schoolsModal}   onClose={()=>setSchoolsModal(false)}/>
            <LabsDetailModal           isOpen={labsModal}      onClose={()=>setLabsModal(false)}/>
            <EquipmentDetailModal      isOpen={equipModal}     onClose={()=>setEquipModal(false)}/>
            <SessionsDetailModal       isOpen={sessionsModal}  onClose={()=>setSessionsModal(false)}/>

            <ArtifactsDetailModal      isOpen={artifactsModal} onClose={()=>setArtifactsModal(false)}/>
            <URLsDetailModal           isOpen={urlsModal}      onClose={()=>setUrlsModal(false)}/>
            <SearchesYearDetailModal   isOpen={searchYrModal}  onClose={()=>setSearchYrModal(false)}/>
            <SearchesMonthDetailModal  isOpen={searchMoModal}  onClose={()=>setSearchMoModal(false)}/>

            {/* ═══ IMAGE VIEWER ═════════════════════════════════ */}
            {viewImgs.length>0 && <ImageViewer imgs={viewImgs} idx={viewIdx} onClose={closeViewer} onNext={nextImg} onPrev={prevImg}/>}
        </main>
    );
};

export default Home;
